import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DashboardCard, { EmptyState } from './DashboardCard';
import { chartColors, fmtInt } from './formatters';

/** Top 10 customers by activations, split into new installations and refreshes. Clicking a row filters the page. */
export default function TopCustomersCard({ customers = [], onSelectCustomer, sx }) {
  const max = Math.max(1, ...customers.map((c) => c.activations));

  return (
    <DashboardCard
      title="Top 10 customers by activations"
      subtitle="Grouped by CustomerID · click a customer to filter the page"
      action={
        <Stack direction="row" sx={{ gap: 2 }}>
          <Legend color={chartColors.newInstalls} label="New" />
          <Legend color={chartColors.refreshes} label="Refresh" />
        </Stack>
      }
      sx={sx}
    >
      {customers.length === 0 ? (
        <EmptyState />
      ) : (
        <Stack sx={{ gap: 0.5 }}>
          {customers.map((c, i) => {
            const refreshes = c.activations - c.newInstalls;
            return (
              <ButtonBase
                key={c.customerId ?? i}
                onClick={() => onSelectCustomer?.(c)}
                aria-label={`${c.company}: ${c.activations} activations, ${c.newInstalls} new. Filter by this customer`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '24px minmax(0, 200px) minmax(0, 1fr) 48px',
                  alignItems: 'center',
                  gap: 1.5,
                  minHeight: 36,
                  px: 1,
                  borderRadius: 1.5,
                  textAlign: 'left',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="caption" color="text.secondary">{i + 1}</Typography>
                <Typography variant="body2" noWrap title={`${c.company} (${c.customerId})`}>{c.company || c.customerId}</Typography>
                <Box sx={{ display: 'flex', height: 14, borderRadius: 0.5, overflow: 'hidden' }}>
                  <Box sx={{ width: `${(c.newInstalls / max) * 100}%`, bgcolor: chartColors.newInstalls }} />
                  <Box sx={{ width: `${(refreshes / max) * 100}%`, bgcolor: chartColors.refreshes }} />
                </Box>
                <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 500 }}>{fmtInt(c.activations)}</Typography>
              </ButtonBase>
            );
          })}
        </Stack>
      )}
    </DashboardCard>
  );
}

function Legend({ color, label }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}
